import React from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress } from '@anytag/shared-atoms';
import { useDeepCompareEffect, useScrollElementBottom, useToggle } from '@anytag/shared-hooks';
import { cn } from '@anytag/shared-utils';
import { UseInitAIAssistant } from '../../hook';
import { useChatRoomContext } from '../chat-room-context';
import { Message } from './message';
import { MessageFileType, MessageType } from './types';

// Continuously messages have same type will be grouped in same row if needed
const SUPPORTED_GROUP_TYPES: MessageFileType['type'][] = ['image'];

const checkGroupFileMessages = (message?: MessageType) =>
  !!message?.file?.type && SUPPORTED_GROUP_TYPES.includes(message.file.type);

export interface MessagesProps
  extends Pick<UseInitAIAssistant, 'chatHistoryThreadId' | 'getChatHistory' | 'isLoadingChatHistory'> {
  messages: MessageType[];
  canScrollToBottom: boolean;
  onRegenerate?: (index: number) => void;
}

export const Messages = ({
  messages,
  onRegenerate,
  getChatHistory,
  canScrollToBottom,
  chatHistoryThreadId,
  isLoadingChatHistory
}: MessagesProps) => {
  const { t } = useTranslation();
  const goDownButton = useToggle();
  const { uploadNewFiles, uploadFileConfigs } = useChatRoomContext();
  const [ref, scrollToBottom] = useScrollElementBottom<HTMLDivElement>([messages]);

  const hasMessages = messages.length > 1 || (messages.length === 1 && !messages.at(0)?.typing);

  // Recheck/Update go down button state when messages list changed, but not for loading chat history
  useDeepCompareEffect(() => {
    if (
      canScrollToBottom &&
      ref.current &&
      goDownButton.on &&
      ref.current.scrollHeight - ref.current.clientHeight === ref.current.scrollTop
    ) {
      goDownButton.setOff();
    }
  }, [messages, goDownButton.on, canScrollToBottom]);

  // Group message in same row if they are files and make them faltten if a message includes both text & file
  const formattedMessages = messages.reduce<(MessageType & { index: number })[][]>((acc, curr, index) => {
    const prevMessages = acc.at(-1);

    // Check if current and prev message can be grouped and must be same position
    if (
      checkGroupFileMessages(curr) &&
      checkGroupFileMessages(prevMessages?.at(-1)) &&
      prevMessages?.at(-1)?.position === curr.position && // Must same left | right position
      prevMessages?.at(-1)?.file?.type === curr.file?.type // Must same file type
    ) {
      acc.at(acc.length - 1)?.push({ ...curr, index });
    } else if (curr.file?.url && curr.text) {
      acc.push([{ ...curr, text: undefined, index }]); // Insert image message first
      acc.push([{ ...curr, file: undefined, index }]);
    } else {
      acc.push([{ ...curr, index }]);
    }

    return acc;
  }, []);

  return (
    <>
      {isLoadingChatHistory && hasMessages ? (
        <div className="flex h-10 items-center justify-center">
          <CircularProgress className="bg-black-30" size={16} thickness={0.5} />
        </div>
      ) : null}

      <Dropzone
        {...uploadFileConfigs}
        onDrop={uploadNewFiles}
        accept={uploadFileConfigs?.accept ? { [uploadFileConfigs.accept]: [] } : undefined}
      >
        {({ isDragAccept, getRootProps, getInputProps }) => (
          <>
            <div
              ref={ref}
              className={styles.wrapper}
              onScroll={(e) => {
                if (
                  e.currentTarget.scrollTop < e.currentTarget.scrollHeight - e.currentTarget.clientHeight - 50 &&
                  !goDownButton.on
                ) {
                  goDownButton.setOn();
                } else if (
                  e.currentTarget.scrollTop + e.currentTarget.clientHeight + 50 > e.currentTarget.scrollHeight &&
                  goDownButton.on
                ) {
                  goDownButton.setOff();
                }

                if (e.currentTarget.scrollTop === 0 && hasMessages && chatHistoryThreadId && !isLoadingChatHistory) {
                  getChatHistory();
                }
              }}
            >
              <div
                className="min-h-inherit h-full"
                // Prevent open upload file window when click the messages list, just allow drag/drop here
                {...getRootProps({ onClick: (event) => event.stopPropagation() })}
              >
                {isDragAccept ? (
                  <div className={styles.draggingWrapper}>
                    <div className={styles.draggingText}>{t('drag and drop a file here')}</div>
                  </div>
                ) : (
                  <div className={styles.messages}>
                    {formattedMessages.map((items, index) => (
                      <React.Fragment key={`message-${index}`}>
                        {items.length > 1 ? (
                          <div className={styles.group(items.at(0)?.position === 'left')}>
                            {items.map((msg, idx) => (
                              <Message key={`group-message-${index}-${idx}`} message={msg} />
                            ))}
                          </div>
                        ) : items.at(0) ? (
                          <Message
                            message={items.at(0)}
                            onRegenerate={() => onRegenerate?.(items.at(0)?.index as number)}
                          />
                        ) : null}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <input name="chatboxUploadInput" {...getInputProps()} />
              </div>
            </div>

            {goDownButton.on ? (
              <Button
                variant="outline"
                onClick={scrollToBottom}
                className={styles.scrollDownButton}
                prefixIcon={{ icon: 'arrow-down', size: 11, variant: 'gray' }}
              />
            ) : null}
          </>
        )}
      </Dropzone>
      <div className="absolute bottom-0 left-0 h-8 w-full bg-gradient-to-b from-transparent to-white" />
    </>
  );
};

const styles = {
  wrapper: cn('max-h-full min-h-full flex-1 overflow-x-hidden overflow-y-auto bg-white', '[scrollbar-width:none]'),
  messages: cn('min-h-inherit box-border flex flex-col gap-4 px-4 py-6'),
  group: (isLeft: boolean) => cn('flex flex-wrap gap-2', isLeft ? 'flex-start' : 'flex-end'),
  draggingWrapper: cn('flex h-full items-center justify-center', 'bg-black-50/50 box-border'),
  draggingText: cn('box-border rounded-md p-2 text-lg font-medium text-white', 'border border-white'),
  scrollDownButton: cn('absolute bottom-2 left-1/2 z-50 size-6 flex-none -translate-x-1/2 rounded-full p-0')
};
